import { Button, type ButtonProps, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import type { FC, ReactElement, ReactNode } from 'react'
import { LuX } from 'react-icons/lu'
import { ConfirmDialogTransition } from './ConfirmDialogTransition'

export type ConfirmDialogContentHelpers = {
    close: () => void
}

export type ConfirmDialogContent = ReactNode | ((helpers: ConfirmDialogContentHelpers) => ReactNode)

export interface ConfirmDialogOptions {
    variant?: 'confirm' | 'info'
    title?: ReactElement | string
    content?: ConfirmDialogContent
    color?: ButtonProps['color']

    onConfirm?: (() => void) | (() => Promise<void>)
    onCancel?: (() => void) | (() => Promise<void>)
    /** Backdrop, Escape, and the X control; only the Cancel button uses `onCancel`. */
    onClickOutside?: (() => void) | (() => Promise<void>)

    confirmButtonText?: string
    cancelButtonText?: string
    maxWidth?: 'xs' | 'sm'
}

type ConfirmDialogProps = {
    open: boolean

    /** When set, shows a loading indicator on that button and disables all actions. */
    pendingAction?: 'confirm' | 'cancel' | null

    /** Fired when the exit transition completes; useful for unmounting the dialog. */
    onExited?: () => void
} & Pick<
    ConfirmDialogOptions,
    'variant' | 'title' | 'content' | 'color' | 'confirmButtonText' | 'cancelButtonText' | 'maxWidth' | 'onClickOutside' | 'onConfirm' | 'onCancel'
>

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
    open,
    variant = 'confirm',
    title = 'Confirm Action',
    content = 'Are you sure you want to perform this action?',
    onConfirm,
    onCancel,
    onClickOutside,
    pendingAction = null,
    onExited,
    color = 'brand',

    confirmButtonText = 'Confirm',
    cancelButtonText,
    maxWidth = 'xs',
}) => {
    const isInfo = variant === 'info'
    const dismissLabel = cancelButtonText ?? (isInfo ? 'Close' : 'Cancel')
    const busy = !!pendingAction

    const dismissWithoutCancelButton = () => {
        if (onClickOutside) {
            onClickOutside()
        } else {
            onCancel?.()
        }
    }

    const renderedContent = typeof content === 'function' ? content({ close: dismissWithoutCancelButton }) : content

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (busy) return
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    dismissWithoutCancelButton()
                } else {
                    onCancel?.()
                }
            }}
            disableEscapeKeyDown={busy}
            maxWidth={maxWidth}
            fullWidth
            slots={{ transition: ConfirmDialogTransition }}
            slotProps={{
                transition: {
                    appear: true,
                    onExited,
                },
                paper: {
                    sx: { borderRadius: '8px' },
                },
            }}
        >
            <DialogTitle className="justify-between! flex! items-start!">
                <div>{title}</div>

                <IconButton size="small" onClick={dismissWithoutCancelButton} className="-mr-1.5!" disabled={busy}>
                    <LuX className="text-base" />
                </IconButton>
            </DialogTitle>
            <DialogContent className="-mt-2! -mb-1!">
                <div>{renderedContent}</div>
            </DialogContent>
            <DialogActions className="px-5! pb-5! gap-2.5!">
                {isInfo ? (
                    <Button
                        variant="contained"
                        color="neutral"
                        disableElevation
                        fullWidth
                        onClick={onCancel}
                        disabled={busy}
                        startIcon={pendingAction === 'cancel' ? <CircularProgress color="inherit" size={18} /> : undefined}
                    >
                        {dismissLabel}
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="neutral"
                            disableElevation
                            fullWidth
                            onClick={onCancel}
                            disabled={busy}
                            startIcon={pendingAction === 'cancel' ? <CircularProgress color="inherit" size={18} /> : undefined}
                        >
                            {dismissLabel}
                        </Button>
                        <Button
                            variant="contained"
                            color={color}
                            disableElevation
                            fullWidth
                            onClick={onConfirm}
                            disabled={busy}
                            loading={pendingAction === 'confirm'}
                            loadingPosition="center"
                        >
                            {confirmButtonText}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}
